/**
 * Centralized keyboard shortcuts configuration.
 *
 * Rules:
 * - Avoid ⇧⌘3/4/5 — they conflict with macOS screenshot shortcuts.
 * - `mod` resolves to ⌘ on Mac, Ctrl on Windows/Linux.
 * - Settings-tab shortcuts use Alt (Option on Mac) + digit to stay conflict-free.
 */

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

/** Modifier symbols for display */
export const MOD_SYMBOL = isMac ? '⌘' : 'Ctrl';
export const SHIFT_SYMBOL = '⇧';
export const ALT_SYMBOL = isMac ? '⌥' : 'Alt';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShortcutDef {
  /** Human-readable label shown in UI (e.g. "⇧⌘L") */
  label: string;
  /** Key used in the keydown handler (lowercase letter or 'Digit1' etc.) */
  key: string;
  /** Which modifier combination is required */
  modifiers: {
    mod: boolean;   // Cmd / Ctrl
    shift: boolean;
    alt: boolean;
  };
  /** Navigation path (absolute) */
  path: string;
  /** Optional description */
  description?: string;
}

// ---------------------------------------------------------------------------
// Global navigation shortcuts  (Cmd/Ctrl + key  or  Cmd/Ctrl+Shift + key)
// ---------------------------------------------------------------------------

export const GLOBAL_SHORTCUTS: ShortcutDef[] = [
  // No-shift shortcuts
  {
    label: `${MOD_SYMBOL},`,
    key: ',',
    modifiers: { mod: true, shift: false, alt: false },
    path: '/settings',
    description: 'Open Settings',
  },

  // Shift shortcuts
  {
    label: `${SHIFT_SYMBOL}${MOD_SYMBOL}N`,
    key: 'n',
    modifiers: { mod: true, shift: true, alt: false },
    path: '/loads/new',
    description: 'New Load',
  },
  {
    label: `${SHIFT_SYMBOL}${MOD_SYMBOL}L`,
    key: 'l',
    modifiers: { mod: true, shift: true, alt: false },
    path: '/loads',
    description: 'Loads',
  },
  {
    label: `${SHIFT_SYMBOL}${MOD_SYMBOL}I`,
    key: 'i',
    modifiers: { mod: true, shift: true, alt: false },
    path: '/invoices',
    description: 'Invoices',
  },
  {
    label: `${SHIFT_SYMBOL}${MOD_SYMBOL}E`,
    key: 'e',
    modifiers: { mod: true, shift: true, alt: false },
    path: '/customers',
    description: 'Customers',
  },
  {
    label: `${SHIFT_SYMBOL}${MOD_SYMBOL}D`,
    key: 'd',
    modifiers: { mod: true, shift: true, alt: false },
    path: '/drivers',
    description: 'Drivers',
  },
  {
    label: `${SHIFT_SYMBOL}${MOD_SYMBOL}O`,
    key: 'o',
    modifiers: { mod: true, shift: true, alt: false },
    path: '/documents',
    description: 'Documents',
  },
];

// ---------------------------------------------------------------------------
// Settings tab shortcuts  (Alt/Option + digit — no macOS conflicts)
// ---------------------------------------------------------------------------

export interface SettingsTabShortcut extends ShortcutDef {
  /** Tab value used in the Tabs component and URL query param */
  tab: string;
}

export const SETTINGS_TAB_SHORTCUTS: SettingsTabShortcut[] = [
  {
    label: `${ALT_SYMBOL}1`,
    key: 'Digit1',
    modifiers: { mod: false, shift: false, alt: true },
    path: '/settings?tab=company',
    tab: 'company',
    description: 'Company settings',
  },
  {
    label: `${ALT_SYMBOL}2`,
    key: 'Digit2',
    modifiers: { mod: false, shift: false, alt: true },
    path: '/settings?tab=users',
    tab: 'users',
    description: 'User management',
  },
  {
    label: `${ALT_SYMBOL}3`,
    key: 'Digit3',
    modifiers: { mod: false, shift: false, alt: true },
    path: '/settings?tab=integrations',
    tab: 'integrations',
    description: 'Integrations',
  },
  {
    label: `${ALT_SYMBOL}4`,
    key: 'Digit4',
    modifiers: { mod: false, shift: false, alt: true },
    path: '/settings?tab=billing',
    tab: 'billing',
    description: 'Billing & subscription',
  },
  {
    label: `${ALT_SYMBOL}5`,
    key: 'Digit5',
    modifiers: { mod: false, shift: false, alt: true },
    path: '/settings?tab=notifications',
    tab: 'notifications',
    description: 'Notification preferences',
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Look up the display label for a global shortcut by its path */
export function getShortcutLabel(path: string): string | undefined {
  return GLOBAL_SHORTCUTS.find(s => s.path === path)?.label;
}

/** Look up the display label for a settings tab shortcut by tab name */
export function getSettingsTabLabel(tab: string): string | undefined {
  return SETTINGS_TAB_SHORTCUTS.find(s => s.tab === tab)?.label;
}
